import { useMemo, useRef, type SyntheticEvent } from "react"
import debounce from "lodash/debounce"

import { useRouter } from "@/lib/router"
import {
  selectAuthFlowEmailVerifyDisplayEmail,
  selectAuthFlowEmailVerifyError,
  selectAuthFlowEmailVerifyMessage,
  selectAuthFlowEmailVerifyResending,
  selectAuthFlowEmailVerifyStep,
  selectAuthFlowEmailVerifyVerifying,
} from "@/lib/storage/auth-slice"
import {
  closeAuthDialog,
  handleEmailVerifyResend,
  handleEmailVerifySubmit,
} from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectUser } from "@/lib/storage/user-slice"
import { Form, FormControl, FormField } from "@/components/ui/form"
import { TextInput } from "@/components/ui/text-input"
import { Button } from "../../components/ui/button"

export default function EmailVerifyComponent() {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const currentUser = useAppSelector(selectUser)
  const step = useAppSelector(selectAuthFlowEmailVerifyStep)
  const verifying = useAppSelector(selectAuthFlowEmailVerifyVerifying)
  const resending = useAppSelector(selectAuthFlowEmailVerifyResending)
  const message = useAppSelector(selectAuthFlowEmailVerifyMessage)
  const error = useAppSelector(selectAuthFlowEmailVerifyError)
  const displayEmail = useAppSelector(selectAuthFlowEmailVerifyDisplayEmail)

  const codeInputRef = useRef<HTMLInputElement>(null)

  const handleSendEmail = () => {
    void dispatch(handleEmailVerifyResend())
  }

  const handleSendEmailRef = useRef(handleSendEmail)
  handleSendEmailRef.current = handleSendEmail
  const debouncedHandleSendEmail = useMemo(
    () =>
      debounce(() => handleSendEmailRef.current(), 500, {
        leading: true,
        trailing: false,
      }),
    []
  )

  const CODE_LENGTH = 6

  const submitCode = () => {
    if (!codeInputRef.current) return

    const code = codeInputRef.current.value
    void dispatch(handleEmailVerifySubmit({ code, router }))

    // Select input on error (handled via useEffect would be better but keeping simple for now)
    if (error) {
      setTimeout(() => codeInputRef.current?.select(), 1)
    }
  }

  const handleCodeInput = (e: SyntheticEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const cursorPos = input.selectionEnd || input.value.length
    const raw = input.value
    const processed = raw
      .toUpperCase()
      .split("")
      .filter((letter) => /[A-Z0-9]/.test(letter))
      .join("")

    input.value = processed.slice(0, CODE_LENGTH)
    const newCursorPos = cursorPos - raw.length + processed.length
    input.setSelectionRange(newCursorPos, newCursorPos)

    // submit the code immediately on paste
    if (
      processed.length === CODE_LENGTH &&
      (e.nativeEvent as InputEvent).inputType === "insertFromPaste"
    ) {
      void submitCode()
    }
  }

  const handleCloseAfterVerified = () => {
    void dispatch(closeAuthDialog())
  }

  return (
    <>
      {step === "verified" ? (
        <>
          <p>Thank you for verifying your email.</p>
          <Button
            variant="outline"
            onClick={handleCloseAfterVerified}
            className="mt-4 mr-auto"
          >
            Close
          </Button>
        </>
      ) : (
        <>
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              void submitCode()
            }}
          >
            <p className="text-pretty">
              Please enter the verification code that was just sent to{" "}
              <span className="no-translate font-semibold">
                {displayEmail ?? currentUser.email}
              </span>
              .
            </p>
            <div className="flex flex-wrap gap-4">
              <FormField name="code" className="w-24 grow">
                <FormControl
                  render={
                    <TextInput
                      className="font-mono tracking-widest"
                      placeholder={"X".repeat(CODE_LENGTH)}
                      ref={codeInputRef}
                      onInput={handleCodeInput}
                      disabled={verifying}
                    />
                  }
                />
              </FormField>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={verifying}
              >
                Continue
              </Button>
            </div>
          </Form>
          {error && (
            <p className="leading-tight font-semibold text-destructive">
              {error}
            </p>
          )}
          {message ? (
            <p className="font-semibold">{message}</p>
          ) : (
            <>
              <p>Didn’t receive the email? Click below to resend:</p>
              <Button
                variant="outline"
                onClick={debouncedHandleSendEmail}
                className="mr-auto"
                disabled={resending}
              >
                Resend email
              </Button>
            </>
          )}
        </>
      )}
    </>
  )
}
